import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';

interface RecentItem {
  type: 'meeting' | 'best-practice';
  filename: string;
  displayName: string;
  dateLabel: string;
  summary: string | null;
}

function WordTablet({ word, bg, color }: { word: string; bg: string; color: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 20px',
        whiteSpace: 'nowrap',
        borderRadius: 12,
        background: bg,
        color: color,
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: '0.03em',
        fontFamily: 'var(--f-h)',
        marginBottom: 8,
      }}
    >
      {word}
    </div>
  );
}

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);

  useEffect(() => {
    client.get<{ items: RecentItem[] }>('/recent')
      .then(({ data }) => setRecentItems(data.items))
      .catch(() => {/* keep empty, fallback to static content */});
  }, []);

  const features = [
    {
      tablet: { word: 'One Voice', bg: '#1a3a5c', color: '#fff' },
      title: 'Industry Collaboration',
      desc: 'A unified voice for retail derivative brokers, enabling knowledge sharing and collective problem-solving.',
    },
    {
      tablet: { word: 'Together', bg: 'var(--c-primary)', color: '#fff' },
      title: 'Regulatory Engagement',
      desc: 'Direct dialogue with the FCA, ASIC, and other global regulators on behalf of member firms.',
    },
    {
      tablet: { word: 'Aligned', bg: 'var(--c-accent)', color: '#fff' },
      title: 'Best Practice',
      desc: 'Practical guidance on UPI generation, margin reporting, notional amounts, and settlement types.',
    },
  ];

  const staticNews = [
    {
      date: 'April 2026',
      tag: 'Event',
      title: 'FCA MiFIR Transaction Reporting Forum',
      body: "A UK MiFID Transaction Reporting Forum took place at the FCA's Stratford office, focused on CP25/32 developments and the FCA's internal restructuring.",
    },
    {
      date: 'March 2026',
      tag: 'Regulatory',
      title: 'Margin & Collateral — FCA Guidance Pending',
      body: 'Following a survey of 15 RDF members on margin practices, the FCA is taking findings to the Bank of England.',
    },
    {
      date: 'February 2026',
      tag: 'Best Practice',
      title: 'UPI Best Practice Paper Updated',
      body: 'The RDF has updated its UPI Best Practice paper, adding CFD payout triggers to several product templates.',
    },
  ];

  const newsItems = recentItems.length > 0
    ? recentItems.map((item) => ({
        date: item.type === 'meeting' ? 'Industry Meeting' : (item.dateLabel || item.displayName),
        tag: item.type === 'meeting' ? 'Meeting' : 'Best Practice',
        title: item.displayName,
        body: item.summary ?? 'Members-only summary available in the portal.',
      }))
    : staticNews;

  return (
    <div className="page">
      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero__content">
            <span className="hero__label">Retail Derivatives Forum</span>
            <h1>Navigating Regulatory Reporting Together</h1>
            <p>
              The RDF brings together leading retail brokers, vendors, consultants and repositories to share best practice, engage with regulators, and navigate the evolving transaction reporting landscape.
            </p>
            <div className="hero__actions">
              <button className="btn btn-outline-white btn-lg hero__members-btn" onClick={() => navigate('/members')}>
                Our Members
              </button>
              <button className="btn btn-accent btn-lg" onClick={() => navigate('/register')}>
                Join the Forum
              </button>
              <button className="btn btn-primary btn-lg hero__login-mobile" onClick={() => navigate('/login')}>
                Log In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="section">
        <div className="container">
          <div>
            <span className="section-label">About the RDF</span>
            <h2 style={{marginBottom: 16}}>A Forum Built by the Industry, for the Industry</h2>
            <p style={{color: 'var(--c-text-muted)', marginBottom: 16, lineHeight: 1.75}}>
              The Retail Derivatives Forum (RDF) is an industry body representing retail derivative brokers globally. We work collaboratively to interpret regulatory obligations, develop best practice guidance, and engage directly with regulators across multiple jurisdictions.
            </p>
            <p style={{color: 'var(--c-text-muted)', lineHeight: 1.75}}>
              The RDF is the primary voice for CFD and spread bet brokers on transaction reporting matters globally.
            </p>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="section--sm" style={{background: 'var(--c-bg)'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: 40}}>
            <span className="section-label">What We Do</span>
            <h2>Key Focus Areas</h2>
          </div>
          <div className="features-grid">
            {features.map(f => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">
                  <WordTablet {...f.tablet} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-label">Latest</span>
              <h2>News &amp; Updates</h2>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/login')}>
              Access Full Portal
            </button>
          </div>
          <div className="news-grid">
            {newsItems.map((n, i) => (
              <div key={i} className="news-card">
                <div className="news-date">{n.date}</div>
                <div className="news-tag">{n.tag}</div>
                <div className="news-title">{n.title}</div>
                <div className="news-body">{n.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section--sm" style={{background: 'var(--c-primary)'}}>
        <div className="container" style={{textAlign: 'center'}}>
          <h2 style={{color: 'white', marginBottom: 12}}>Members-only resources available online</h2>
          <p style={{color: 'rgba(255,255,255,.75)', marginBottom: 28, maxWidth: 500, margin: '0 auto 28px'}}>
            Access meeting minutes, best practice papers, and regulatory discussion threads through the RDF member portal.
          </p>
          <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'}}>
            <button className="btn btn-outline-white btn-lg" onClick={() => navigate('/login')}>
              Log In
            </button>
            <button className="btn btn-accent btn-lg" onClick={() => navigate('/register')}>
              Register Your Firm
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
